import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './TooltipLayer.css';

/**
 * ONE tooltip for the whole app, rendered at body level.
 *
 * Any element with a `data-tip` attribute gets it on hover and keyboard focus; an optional
 * `data-tip-placement` of top | bottom | left | right says which side it prefers (default top),
 * and it flips when that side has no room. Nothing else is needed at the call site.
 *
 * Why a portal: every tooltip used to be a CSS `::after` bubble positioned inside its own
 * element, which is clipped by the first ancestor that scrolls or hides overflow — a table
 * header, a card, a sticky bar. The bubble on the Employee list's salary warning was cut in half
 * by the grid header; the action-icon tooltips vanished at the bottom of every scrolling table.
 * Rendered into <body> with fixed positioning, the bubble is never inside anything that clips.
 */
type Placement = 'top' | 'bottom' | 'left' | 'right';
const GAP = 8;
const MARGIN = 6;

interface TipState { text: string; anchor: Element; placement: Placement }

export const TooltipLayer: React.FC = () => {
  const [tip, setTip] = useState<TipState | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number; placement: Placement } | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const show = (target: EventTarget | null) => {
      const el = (target as Element | null)?.closest?.('[data-tip]');
      const text = el?.getAttribute('data-tip')?.trim();
      if (!el || !text) return;
      const placement = (el.getAttribute('data-tip-placement') as Placement | null) ?? 'top';
      setTip({ text, anchor: el, placement });
    };
    const hide = () => setTip(null);
    const onOver = (e: MouseEvent) => show(e.target);
    const onOut = (e: MouseEvent) => {
      const from = (e.target as Element | null)?.closest?.('[data-tip]');
      const to = (e.relatedTarget as Element | null)?.closest?.('[data-tip]');
      if (from && from !== to) hide();
    };
    const onFocusIn = (e: FocusEvent) => show(e.target);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') hide(); };
    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', hide);
    // A click usually changes what is under the pointer (a row deleted, a modal opened);
    // the tooltip must not linger over the new content.
    document.addEventListener('click', hide, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('click', hide, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, []);

  useLayoutEffect(() => {
    if (!tip || !bubbleRef.current) { setPos(null); return; }
    if (!tip.anchor.isConnected) { setTip(null); return; }
    const a = tip.anchor.getBoundingClientRect();
    const b = bubbleRef.current.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    let placement = tip.placement;
    // Flip to the opposite side when the preferred one cannot fit.
    if (placement === 'top' && a.top - b.height - GAP < MARGIN) placement = 'bottom';
    else if (placement === 'bottom' && a.bottom + b.height + GAP > vh - MARGIN) placement = 'top';
    else if (placement === 'left' && a.left - b.width - GAP < MARGIN) placement = 'right';
    else if (placement === 'right' && a.right + b.width + GAP > vw - MARGIN) placement = 'left';
    let left: number, top: number;
    switch (placement) {
      case 'bottom': left = a.left + a.width / 2 - b.width / 2; top = a.bottom + GAP; break;
      case 'left':   left = a.left - b.width - GAP; top = a.top + a.height / 2 - b.height / 2; break;
      case 'right':  left = a.right + GAP; top = a.top + a.height / 2 - b.height / 2; break;
      default:       left = a.left + a.width / 2 - b.width / 2; top = a.top - b.height - GAP;
    }
    // Keep the bubble on screen horizontally; the arrow stays centred on the anchor.
    left = Math.max(MARGIN, Math.min(left, vw - b.width - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - b.height - MARGIN));
    setPos({ left, top, placement });
  }, [tip]);

  if (!tip) return null;
  const arrowX = pos ? Math.max(10, Math.min(
    tip.anchor.getBoundingClientRect().left + tip.anchor.getBoundingClientRect().width / 2 - pos.left,
    (bubbleRef.current?.offsetWidth ?? 20) - 10)) : 0;
  return createPortal(
    <div
      ref={bubbleRef}
      role="tooltip"
      className={`app-tip app-tip--${pos?.placement ?? tip.placement}${pos ? ' app-tip--shown' : ''}`}
      style={pos ? { left: pos.left, top: pos.top, ['--tip-arrow-x' as string]: `${arrowX}px` } : { left: -9999, top: -9999 }}
    >
      {tip.text}
    </div>,
    document.body,
  );
};

export default TooltipLayer;
